import React from 'react'
import { NavLink } from 'react-router-dom'
import { Home, User, LogOut, Mic, Menu, X, CreditCard, Megaphone, ExternalLink, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react'
import Button from '../ui/button'
import { useAuth } from '../../hooks/use-auth'
import { useRecentEpisodes } from '../../lib/recent-episodes'
import { isLegacyPlan, isWeeklyPlan, minutesForCredits } from '../../lib/plans'
import Popover from '../ui/popover'
import Tooltip from '../ui/tooltip'
import { getLatestChangelogEntries, marketingChangelogUrlForId, getLastSeenChangelogId, setLastSeenChangelogId, getRecentChangelogCount, recentChangelogLabel } from '../../lib/changelog'
import { cn } from '../../lib/utils'
import { useNavCollapsed, setNavCollapsed } from '../../lib/nav-collapsed'

const useResponsiveNavDefault = () => {
  const isCollapsed = useNavCollapsed()
  const toggleCollapsed = React.useCallback(() => {
    setNavCollapsed(!isCollapsed)
  }, [isCollapsed])
  return { isCollapsed, toggleCollapsed }
}

const Navigation: React.FC = () => {
  const { user, logout } = useAuth()
  const [isMobileOpen, setIsMobileOpen] = React.useState(false)
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const { isCollapsed, toggleCollapsed } = useResponsiveNavDefault()

  const handleLogout = async () => {
    await logout()
  }

  type NavItem = {
    to: string
    icon: React.ElementType
    label: string
    isNew?: boolean
    isExternal?: boolean
  }

  const navItems: NavItem[] = [
    { to: '/', icon: Home, label: 'Home' },
    ...(!isLegacyPlan(user?.plan) ? [{ to: '/series', icon: Sparkles, label: 'Series', isNew: true }] : []),
    ...(isLegacyPlan(user?.plan) ? [{ to: '/podcasts', icon: Mic, label: 'Podcasts' }] : []),
    { to: '/billing', icon: CreditCard, label: 'Billing' },
  ]

  const usageBadge = (() => {
    if (!user) return null
    const plan = user.plan
    if (isLegacyPlan(plan) && typeof user.creditsRemaining === 'number' && typeof user.creditsCap === 'number') {
      const value = `${minutesForCredits(user.creditsRemaining)}/${minutesForCredits(user.creditsCap)}`
      return { label: 'Minutes', value, tooltip: `${value} minutes` }
    }
    if (isWeeklyPlan(plan) && typeof user.episodesPerCycle === 'number') {
      const cap = user.episodesPerCycle
      const remaining = Math.max(0, cap - (user.episodesUsedThisCycle ?? 0)) + (user.bonusEpisodes ?? 0)
      const value = `${remaining}/${cap}`
      return { label: 'Episodes', value, tooltip: `${value} episodes this week` }
    }
    // Free / unknown: show single free episode quota
    const remaining = user.freeSeriesEpisodeUsed ? 0 : 1
    const value = `${remaining}/1`
    return { label: 'Free episode', value, tooltip: `${value} free episode` }
  })()
  const recentEpisodes = useRecentEpisodes()
  const changelogItems = React.useMemo(() => getLatestChangelogEntries(5), [])
  const recentChangelogCount = React.useMemo(() => getRecentChangelogCount(14), [])
  const latestChangelogId = changelogItems[0]?.id ?? null
  const [lastSeenChangelogId, setLastSeenChangelogIdState] = React.useState<string | null>(null)

  React.useEffect(() => {
    setLastSeenChangelogIdState(getLastSeenChangelogId())
  }, [])
  const showBadge = !!latestChangelogId && lastSeenChangelogId !== latestChangelogId && recentChangelogCount > 0

  const handleChangelogOpenChange = React.useCallback((open: boolean) => {
    if (open && latestChangelogId) {
      setLastSeenChangelogId(latestChangelogId)
      setLastSeenChangelogIdState(latestChangelogId)
    }
  }, [latestChangelogId])

  // Close on Escape
  React.useEffect(() => {
    if (!isMobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isMobileOpen])

  // Focus close on open
  React.useEffect(() => {
    if (isMobileOpen) closeButtonRef.current?.focus()
  }, [isMobileOpen])

  return (
    <>
      {/* Mobile top bar */}
      <header className="md:hidden fixed inset-x-0 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-black/10">
        <div className="h-14 px-4 flex items-center justify-between">
          <button
            type="button"
            aria-label="Open menu"
            aria-controls="mobile-nav-drawer"
            aria-expanded={isMobileOpen}
            onClick={() => setIsMobileOpen(true)}
            className="-m-2 p-2 rounded-md hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Menu className="h-5 w-5 text-foreground" aria-hidden="true" />
          </button>
          <NavLink to="/" className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <img src="/icon.svg" alt="Podcast Generator" className="h-8 w-8" />
            <span className="font-semibold text-foreground">podcast-generator.ai</span>
          </NavLink>
          <div className="w-9" aria-hidden="true" />
        </div>
      </header>

      {/* Desktop sidebar */}
      <nav className={cn(
        "hidden md:flex fixed left-0 top-0 h-screen bg-white border-r border-black/10 flex-col overflow-hidden transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-64"
      )}>
        {/* Header */}
        <div className={cn(
          "border-b border-black/10 transition-all duration-300",
          isCollapsed ? "p-3" : "p-6"
        )}>
          {isCollapsed ? (
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label="Expand navigation"
              className="w-full p-2 rounded-md hover:bg-black/5 text-foreground/60 hover:text-foreground transition-colors flex items-center justify-center"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex items-center justify-between">
              <NavLink to="/" className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity min-w-0">
                <img src="/icon.svg" alt="Podcast Generator" className="h-8 w-8 shrink-0" />
                <span className="font-semibold text-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  podcast-generator.ai
                </span>
              </NavLink>
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label="Collapse navigation"
                className="p-1.5 rounded-md hover:bg-black/5 text-foreground/60 hover:text-foreground transition-colors shrink-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <div className={cn(
          "flex-1 overflow-y-auto transition-all duration-300",
          isCollapsed ? "px-2 py-4" : "px-4 py-4"
        )}>
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isItemDisabled = false
              const commonDisabledClasses = 'opacity-40 cursor-not-allowed pointer-events-none'

              // For external links (like mailto), use anchor tag
              if (item.isExternal) {
                const linkContent = (
                  <a
                    href={item.to}
                    className={cn(
                      'flex items-center rounded-lg text-sm font-medium transition-colors text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent',
                      isCollapsed ? 'justify-center p-2 aspect-square' : 'gap-3 px-3 py-2'
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </a>
                )
                return (
                  <li key={item.to}>
                    {isCollapsed ? (
                      <Tooltip content={item.label} side="right" align="center">
                        {linkContent}
                      </Tooltip>
                    ) : (
                      linkContent
                    )}
                  </li>
                )
              }

              const navLinkContent = (
                <NavLink viewTransition
                  to={item.to}
                  aria-disabled={isItemDisabled}
                  end={item.to === '/podcasts'}
                  onClick={(e) => {
                    if (isItemDisabled) {
                      e.preventDefault()
                    }
                  }}
                  className={({ isActive }) => {
                    if (item.isNew) {
                      const activeClasses = 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200 shadow-sm'
                      const inactiveClasses = 'bg-gradient-to-r from-purple-50/80 to-blue-50/80 text-purple-700 hover:from-purple-100 hover:to-blue-100 border border-purple-200/50 shadow-sm hover:shadow-md hover:scale-[1.02]'
                      return cn(
                        'relative flex items-center rounded-lg text-sm font-medium transition-all duration-200',
                        isCollapsed ? 'justify-center p-2 aspect-square' : 'gap-3 px-3 py-2',
                        isActive ? activeClasses : inactiveClasses,
                        isItemDisabled && commonDisabledClasses
                      )
                    }
                    const activeClasses = 'bg-brand-50 text-brand-700 border border-brand-200'
                    const inactiveClasses = 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                    return cn(
                      'flex items-center rounded-lg text-sm font-medium transition-colors',
                      isCollapsed ? 'justify-center p-2 aspect-square' : 'gap-3 px-3 py-2',
                      isActive ? activeClasses : inactiveClasses,
                      isItemDisabled && commonDisabledClasses
                    )
                  }}
                >
                  <item.icon className={cn('h-4 w-4 shrink-0', item.isNew && 'text-purple-600')} aria-hidden="true" />
                  {!isCollapsed && (
                    <span className="flex items-center justify-between flex-1">
                      <span>{item.label}</span>
                      {item.isNew && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-sm">
                          NEW
                        </span>
                      )}
                    </span>
                  )}
                </NavLink>
              )

              return (
                <li key={item.to}>
                  {isCollapsed ? (
                    <Tooltip content={item.label} side="right" align="center">
                      {navLinkContent}
                    </Tooltip>
                  ) : (
                    navLinkContent
                  )}
                </li>
              )
            })}
            {user?.isAdmin && (
              <li>
                {(() => {
                  const isAdminActive = window.location.pathname.startsWith('/admin')
                  const adminContent = (
                    <NavLink viewTransition
                      to="/admin/users"
                      className={() => cn(
                        'flex items-center rounded-lg text-sm font-medium transition-colors',
                        isCollapsed ? 'justify-center p-2 aspect-square' : 'gap-3 px-3 py-2',
                        isAdminActive
                          ? 'bg-brand-50 text-brand-700 border border-brand-200'
                          : 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                      )}
                    >
                      <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {!isCollapsed && <span>Admin</span>}
                    </NavLink>
                  )
                  return isCollapsed ? (
                    <Tooltip content="Admin" side="right" align="center">
                      {adminContent}
                    </Tooltip>
                  ) : adminContent
                })()}
              </li>
            )}
          </ul>

          {/* Recent Episodes */}
          {!isCollapsed && recentEpisodes.length > 0 && (
            <div className="mt-8">
              <div className="px-3 mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/50">Recent Episodes</p>
              </div>
              <ul className="space-y-1">
                {recentEpisodes.slice(0, 3).map(ep => (
                  <li key={ep.id}>
                    <NavLink viewTransition
                      to={`/podcasts/${ep.id}`}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive
                          ? 'bg-brand-50 text-brand-700 border border-brand-200'
                          : 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                        }`
                      }
                    >
                      <span className="truncate flex-1">{ep.title}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Changelog action pinned above profile */}
        <div className={cn("pb-2 transition-all duration-300", isCollapsed ? "px-2" : "px-4")}>
          <Popover
            onOpenChange={handleChangelogOpenChange}
            trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => {
              const changelogButton = (
                <button
                  ref={ref as any}
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={ariaExpanded}
                  aria-label={isCollapsed ? "What's new" : undefined}
                  onClick={onClick}
                  className={cn(
                    "relative w-full flex items-center rounded-lg text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-200 transition-colors cursor-pointer",
                    isCollapsed ? "justify-center p-2 aspect-square" : "gap-2 px-3 py-2"
                  )}
                >
                  <Megaphone className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {!isCollapsed && <span>What's new</span>}
                  {showBadge && (
                    <span
                      className={cn(
                        "absolute inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-brand-600 text-white text-[10px] leading-none",
                        isCollapsed ? "-top-1 -right-1" : "-top-1.5 -right-1.5"
                      )}
                      aria-label={recentChangelogLabel(recentChangelogCount)}
                      title={recentChangelogLabel(recentChangelogCount)}
                    >
                      {recentChangelogCount}
                    </span>
                  )}
                </button>
              )
              return isCollapsed ? (
                <Tooltip content="What's new" side="right" align="center">
                  {changelogButton}
                </Tooltip>
              ) : changelogButton
            }}
            side="right"
            align="start"
            className="max-w-[18rem] max-h-[70vh] overflow-auto"
          >
            <div className="flex flex-col gap-2">
              <div className="px-1 pb-1 border-b border-black/10">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground/50">Latest updates</p>
              </div>
              {changelogItems.length === 0 ? (
                <p className="text-foreground/60 text-sm">No updates yet.</p>
              ) : (
                <ul className="space-y-1">
                  {changelogItems.map(ci => (
                    <li key={ci.id}>
                      <a
                        href={marketingChangelogUrlForId(ci.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="group block rounded-lg p-2 hover:bg-black/5 cursor-pointer"
                      >
                        <p className="text-sm flex justify-between font-medium text-foreground group-hover:text-foreground/90 truncate flex items-center gap-2">
                          {ci.title}
                          <ExternalLink className="h-3.5 w-3.5 text-foreground/40 group-hover:text-foreground/60" />
                        </p>
                        {ci.short_description && (
                          <p className="text-xs text-foreground/60 truncate">{ci.short_description}</p>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-2">
                <a
                  href="https://podcast-generator.ai/changelog"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-brand-700 hover:underline cursor-pointer"
                >
                  View all updates
                </a>
              </div>
            </div>
          </Popover>
        </div>

        {/* Profile Section */}
        <div className={cn("border-t border-black/10 transition-all duration-300", isCollapsed ? "px-2 py-4" : "px-4 py-4")}>
          {isCollapsed ? (
            // Collapsed: just user icon + tooltip (no gray background)
            <>
              <Tooltip content={user?.name || 'User'} side="right" align="center">
                <button className="w-full flex items-center justify-center p-2 aspect-square rounded-lg hover:bg-black/5 transition-colors cursor-pointer">
                  <div className="h-8 w-8 rounded-full bg-brand-600/10 flex items-center justify-center text-brand-700 ring-1 ring-inset ring-brand-600/20">
                    <User className="h-4 w-4" aria-hidden="true" />
                  </div>
                </button>
              </Tooltip>

              {/* Usage badge (collapsed) */}
              {usageBadge && (
                <Tooltip
                  content={usageBadge.tooltip}
                  side="right"
                  align="center"
                >
                  <NavLink
                    to="/billing"
                    className="mt-2 flex items-center justify-center rounded-lg border border-black/10 bg-white p-2 aspect-square text-xs text-foreground/80 hover:bg-black/5 cursor-pointer transition-colors"
                  >
                    <CreditCard className="h-4 w-4" />
                  </NavLink>
                </Tooltip>
              )}

              {/* Sign out (collapsed) */}
              <Tooltip content="Sign out" side="right" align="center">
                <button
                  onClick={handleLogout}
                  className="mt-2 w-full flex items-center justify-center p-2 aspect-square rounded-lg text-foreground/70 hover:text-foreground hover:bg-black/5 transition-colors cursor-pointer"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            </>
          ) : (
            // Expanded: full profile section
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-black/2.5">
                <div className="h-8 w-8 rounded-full bg-brand-600/10 flex items-center justify-center text-brand-700 ring-1 ring-inset ring-brand-600/20">
                  <User className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-foreground/60 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* Usage badge */}
              {usageBadge && (
                <NavLink
                  to="/billing"
                  className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-foreground/80 hover:bg-black/5 cursor-pointer"
                >
                  <span>{usageBadge.label}</span>
                  <span className="font-medium text-foreground">{usageBadge.value}</span>
                </NavLink>
              )}

              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="w-full justify-start text-foreground/70 hover:text-foreground"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </Button>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" aria-labelledby="mobile-nav-title" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsMobileOpen(false)}
            className="fixed inset-0 bg-black/30 backdrop-blur-[1px]"
          />

          {/* Panel */}
          <div id="mobile-nav-drawer" className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl border-r border-black/10 flex flex-col">
            <div className="h-14 px-3 flex items-center justify-between border-b border-black/10">
              <NavLink to="/" onClick={() => setIsMobileOpen(false)} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                <img src="/icon.svg" alt="Podcast Generator" className="h-8 w-8" />
                <span id="mobile-nav-title" className="font-semibold text-foreground">Menu</span>
              </NavLink>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Close menu"
                onClick={() => setIsMobileOpen(false)}
                className="-m-2 p-2 rounded-md hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto">
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.to}>
                    {(() => {
                      const isItemDisabled = false
                      const commonDisabledClasses = 'opacity-40 cursor-not-allowed pointer-events-none'

                      // For external links (like mailto), use anchor tag
                      if (item.isExternal) {
                        return (
                          <a
                            href={item.to}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent"
                            onClick={() => setIsMobileOpen(false)}
                          >
                            <item.icon className="h-4 w-4" aria-hidden="true" />
                            <span>{item.label}</span>
                          </a>
                        )
                      }

                      return (
                        <NavLink viewTransition
                          to={isItemDisabled ? '#' : item.to}
                          end={item.to === '/podcasts'}
                          aria-disabled={isItemDisabled}
                          onClick={(e) => {
                            if (isItemDisabled) {
                              e.preventDefault()
                            } else {
                              setIsMobileOpen(false)
                            }
                          }}
                          className={({ isActive }) => {
                            if (item.isNew) {
                              const activeClasses = 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200 shadow-sm'
                              const inactiveClasses = 'bg-gradient-to-r from-purple-50/80 to-blue-50/80 text-purple-700 hover:from-purple-100 hover:to-blue-100 border border-purple-200/50 shadow-sm hover:shadow-md hover:scale-[1.02]'
                              return `relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? activeClasses : inactiveClasses} ${isItemDisabled ? commonDisabledClasses : ''}`
                            }
                            const activeClasses = 'bg-brand-50 text-brand-700 border border-brand-200'
                            const inactiveClasses = 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                            return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? activeClasses : inactiveClasses} ${isItemDisabled ? commonDisabledClasses : ''}`
                          }}
                        >
                          <item.icon className={`h-4 w-4 ${item.isNew ? 'text-purple-600' : ''}`} aria-hidden="true" />
                          <span className="flex items-center justify-between flex-1">
                            <span>{item.label}</span>
                            {item.isNew && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-sm">
                                NEW
                              </span>
                            )}
                          </span>
                        </NavLink>
                      )
                    })()}
                  </li>
                ))}
                {user?.isAdmin && (
                  <li>
                    <NavLink viewTransition
                      to="/admin/users"
                      onClick={() => setIsMobileOpen(false)}
                      className={() => {
                        const isAdminActive = window.location.pathname.startsWith('/admin')
                        return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isAdminActive
                            ? 'bg-brand-50 text-brand-700 border border-brand-200'
                            : 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                          }`
                      }}
                    >
                      <User className="h-4 w-4" aria-hidden="true" />
                      Admin
                    </NavLink>
                  </li>
                )}
              </ul>

              {recentEpisodes.length > 0 && (
                <div className="mt-8">
                  <div className="px-3 mb-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/50">Recent Episodes</p>
                  </div>
                  <ul className="space-y-1">
                    {recentEpisodes.slice(0, 3).map(ep => (
                      <li key={ep.id}>
                        <NavLink viewTransition
                          to={`/podcasts/${ep.id}`}
                          onClick={() => setIsMobileOpen(false)}
                          className={({ isActive }) =>
                            `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive
                              ? 'bg-brand-50 text-brand-700 border border-brand-200'
                              : 'text-foreground/70 hover:text-foreground hover:bg-black/5 border border-transparent'
                            }`
                          }
                        >
                          <span className="truncate flex-1">{ep.title}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Pinned changelog above profile in mobile */}
            <div className="p-4 border-t border-black/10">
              <Popover
                onOpenChange={handleChangelogOpenChange}
                trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                  <button
                    ref={ref as any}
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={ariaExpanded}
                    onClick={onClick}
                    className="relative w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-200 transition-colors cursor-pointer"
                  >
                    <Megaphone className="h-4 w-4" aria-hidden="true" />
                    What's new
                    {showBadge && (
                      <span
                        className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-brand-600 text-white text-[10px] leading-none"
                        aria-label={recentChangelogLabel(recentChangelogCount)}
                        title={recentChangelogLabel(recentChangelogCount)}
                      >
                        {recentChangelogCount}
                      </span>
                    )}
                  </button>
                )}
                side="right"
                align="start"
                className="max-w-[18rem] max-h-[70vh] overflow-auto"
              >
                <div className="flex flex-col gap-2">
                  <div className="px-1 pb-1 border-b border-black/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/50">Latest updates</p>
                  </div>
                  {changelogItems.length === 0 ? (
                    <p className="text-foreground/60 text-sm">No updates yet.</p>
                  ) : (
                    <ul className="space-y-1">
                      {changelogItems.map(ci => (
                        <li key={ci.id}>
                          <a
                            href={marketingChangelogUrlForId(ci.id)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => setIsMobileOpen(false)}
                            className="group block rounded-lg p-2 hover:bg-black/5 cursor-pointer"
                          >
                            <p className="text-sm flex justify-between font-medium text-foreground group-hover:text-foreground/90 truncate flex items-center gap-2">
                              {ci.title}
                              <ExternalLink className="h-3.5 w-3.5 text-foreground/40 group-hover:text-foreground/60" />
                            </p>
                            {ci.short_description && (
                              <p className="text-xs text-foreground/60 truncate">{ci.short_description}</p>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="pt-2">
                    <a
                      href="https://podcast-generator.ai/changelog"
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsMobileOpen(false)}
                      className="text-xs font-medium text-brand-700 hover:underline cursor-pointer"
                    >
                      View all updates
                    </a>
                  </div>
                </div>
              </Popover>
            </div>

            <div className="p-4 border-t border-black/10">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-black/2.5">
                <div className="h-8 w-8 rounded-full bg-brand-600/10 flex items-center justify-center text-brand-700 ring-1 ring-inset ring-brand-600/20">
                  <User className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-foreground/60 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>

              {usageBadge && (
                <NavLink
                  to="/billing"
                  onClick={() => setIsMobileOpen(false)}
                  className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-foreground/80 hover:bg-black/5 cursor-pointer"
                >
                  <span>{usageBadge.label}</span>
                  <span className="font-medium text-foreground">{usageBadge.value}</span>
                </NavLink>
              )}
              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIsMobileOpen(false); handleLogout() }}
                  className="w-full justify-start text-foreground/70 hover:text-foreground"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navigation
